import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { globalCampaignsApi } from 'api/endpoints/global_campaigns.api';
import { Campaign } from 'types/push.types';
import {
  GlobalCampaignCreateRequest,
  GlobalCampaignUpdateRequest,
} from 'types/global_campaigns.types';
import { RootState } from 'store';

interface GlobalCampaignsState {
  campaigns: Campaign[];
  currentCampaign: Campaign | null;
  loading: boolean;
  error: string | null;
}

const initialState: GlobalCampaignsState = {
  campaigns: [],
  currentCampaign: null,
  loading: false,
  error: null,
};

export const fetchGlobalCampaigns = createAsyncThunk(
  'globalCampaigns/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await globalCampaignsApi.list();
      return response.campaigns;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось загрузить глобальные кампании',
      );
    }
  },
);

export const fetchGlobalCampaign = createAsyncThunk(
  'globalCampaigns/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await globalCampaignsApi.get(id);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось загрузить кампанию',
      );
    }
  },
);

export const createGlobalCampaign = createAsyncThunk(
  'globalCampaigns/create',
  async (data: GlobalCampaignCreateRequest, { rejectWithValue }) => {
    try {
      const response = await globalCampaignsApi.create(data);
      return response.campaign;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось создать кампанию',
      );
    }
  },
);

export const updateGlobalCampaign = createAsyncThunk(
  'globalCampaigns/update',
  async (
    { id, data }: { id: number; data: GlobalCampaignUpdateRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await globalCampaignsApi.update(id, data);
      return response.campaign;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось обновить кампанию',
      );
    }
  },
);

export const deleteGlobalCampaign = createAsyncThunk(
  'globalCampaigns/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await globalCampaignsApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось удалить кампанию',
      );
    }
  },
);

const globalCampaignsSlice = createSlice({
  name: 'globalCampaigns',
  initialState,
  reducers: {
    setCurrentGlobalCampaign: (state, action: PayloadAction<Campaign | null>) => {
      state.currentCampaign = action.payload;
    },
    clearGlobalCampaignsError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGlobalCampaigns.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload || [];
      })
      .addCase(fetchGlobalCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchGlobalCampaign.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
      })
      .addCase(fetchGlobalCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createGlobalCampaign.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGlobalCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload);
        state.currentCampaign = action.payload;
      })
      .addCase(createGlobalCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateGlobalCampaign.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGlobalCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
        const idx = state.campaigns.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) {
          state.campaigns[idx] = action.payload;
        }
      })
      .addCase(updateGlobalCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(deleteGlobalCampaign.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGlobalCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(c => c.id !== action.payload);
        if (state.currentCampaign?.id === action.payload) {
          state.currentCampaign = null;
        }
      })
      .addCase(deleteGlobalCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentGlobalCampaign,
  clearGlobalCampaignsError,
} = globalCampaignsSlice.actions;

export const selectGlobalCampaigns = (state: RootState) =>
  state.globalCampaigns.campaigns;
export const selectCurrentGlobalCampaign = (state: RootState) =>
  state.globalCampaigns.currentCampaign;
export const selectGlobalCampaignsLoading = (state: RootState) =>
  state.globalCampaigns.loading;
export const selectGlobalCampaignsError = (state: RootState) =>
  state.globalCampaigns.error;

export default globalCampaignsSlice.reducer;